<template>
  <modal
    elem-id="modal_so_delivery_date_change"
    :show="isVisible"
    :title="t('Alterar data de entrega')"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isSaving ? t('Salvando') : t('Salvar nova data')"
    save-test-id="so-delivery-date-change-save-btn"
    :save-fn="handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible && currentItemId">
      <SODeliveryDateChangeForm
        ref="formRef"
        :order-item-id="currentItemId"
        @submit-success="handleFormSuccess"
        @submit-error="handleFormError"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import type { Orders_UpdateItemDeliveryDateResponseDados } from '../../contracts/Orders_UpdateItemDeliveryDateResponse';

const { t } = useI18n();

interface FormInstance {
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
  validateForm: () => boolean;
}

interface ModalOpenPayload {
  itemId: number;
}

const isVisible = ref(false);
const currentItemId = ref<number | null>(null);
const formRef = ref<FormInstance | null>(null);
const isSaving = ref(false);

function openModal(data?: ModalOpenPayload): void {
  log('SODeliveryDateChangeModal', 'openModal', 'Opening', data);
  currentItemId.value = data?.itemId ?? null;
  isVisible.value = true;
}

function handleClose(): void {
  log('SODeliveryDateChangeModal', 'handleClose', 'Closing');
  isVisible.value = false;
  currentItemId.value = null;
  isSaving.value = false;
  if (formRef.value) {
    formRef.value.resetForm();
  }
}

async function handleSave(): Promise<void> {
  if (!formRef.value) return;
  isSaving.value = true;
  try {
    await formRef.value.handleSubmit();
  } finally {
    isSaving.value = false;
  }
}

function handleFormSuccess(result: Orders_UpdateItemDeliveryDateResponseDados): void {
  const itemId = currentItemId.value;
  log('SODeliveryDateChangeModal', 'handleFormSuccess', 'Saved', { itemId });
  eventBus.emit('so-order-item.delivery-date.updated', { itemId, result });
  eventBus.emit('globalMessage.success', t('Data de entrega atualizada'));
  handleClose();
}

function handleFormError(err: unknown): void {
  logError('SODeliveryDateChangeModal', 'handleFormError', 'Submit error', err);
}

onMounted(() => {
  eventBus.on('modal.so-delivery-date-change.open', openModal);
  eventBus.on('modal.so-delivery-date-change.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.so-delivery-date-change.open', openModal);
  eventBus.off('modal.so-delivery-date-change.close', handleClose);
});

defineExpose({ openModal });
</script>

<style scoped>
</style>
